<template>
	<div class="block-options pr">
		<strong class="title db f5 fw5">Advanced</strong>

		<div class="field-area">
			<span class="label-holder">
				<label for="cloudName">Cloud name</label>
			</span>
			<div class="field">
				<input type="text" placeholder="Your cloud name" id="cloudName"
					   :value="cloudName" @input="onCloudNameChange"
					   v-on:keyup.enter="onTextEnter"
					   :class="{error: cloudPresetInputError}"
					   ref="cloudName">
			</div>
		</div>

		<div class="field-area">
			<span class="label-holder">
				<label for="uploadPreset">Upload preset</label>
			</span>
			<div class="field">
				<input type="text" placeholder="Your upload preset" id="uploadPreset"
					   :value="uploadPreset" @input="onPresetChange"
					   v-on:keyup.enter="onTextEnter"
					   :class="{error: cloudPresetInputError}"
					   ref="uploadPreset">
			</div>
		</div>

		<div class="button-container flex w-100 justify-center">
			<Button text="Apply" :icon="checkIcon" :color="blue"
					@click.native="onTextFieldsApply" :disabled="!cloudPresetApplyEnabled"/>
		</div>

		<Checkbox v-if="modalSupported" id="chk-modal" label="Inline widget" :checked="!modal"
				  param="modal" v-on:change="onModalChange"/>

		<Checkbox id="chk-cropping" label="Show cropping" :checked="params.cropping"
				  param="cropping" v-on:change="onBooleanChange"/>

		<Checkbox id="chk-multiple" label="Allow multiple files" :checked="params.multiple"
				  param="multiple" v-on:change="onBooleanChange"/>

		<Checkbox id="chk-advanced" label="Show advanced options" :checked="params.showAdvancedOptions"
				  param="showAdvancedOptions" v-on:change="onBooleanChange"/>

		<div :class="[`flex items-center`]">
			<span class="label-holder">
				<label for="cloudName">Default source</label>
			</span>
			<div class="select-holder">
					<span class="fake-select">
						<select v-on:change="onDefaultChange">
							<option v-for="(src) in sources" :value="src" :key="src"
									:selected="src === params.defaultSource">{{src}}</option>
						</select>
					</span>
			</div>
		</div>
	</div>
</template>

<script>
	import {mapActions, mapState} from "vuex";
	import trim from "lodash/trim";
	import consts from "@/common/consts";
	import {types as widgetTypes} from "@/store/modules/widget";
	import {types as appTypes} from "@/store/modules/app";
	import {CHECK as checkIcon} from "@/assets/icons";
	import Button from "../simple/Button";
	import Checkbox from "../simple/Checkbox";

	const isNullOrUndefined = (val) => val === null || typeof val === "undefined";

	export default {
		name: "AdvancedParams",
		components: {
			Checkbox,
			Button,
		},

		data() {
			return {
				checkIcon: checkIcon,
				blue: consts.BTN_COLORS.BLUE,
				cloudPresetInputError: false,
				cloudPresetApplyEnabled: false,
			};
		},

		computed: {
			...mapState({
				modalSupported: (state) => state.widget.modalSupported,
				params: (state) => state.widget.advanced,
				modal: (state) => state.widget.modal,
				sources: (state) => state.widget.activeSources,
				cloudName: (state) => isNullOrUndefined(state.widget.newCloudName) ? state.widget.cloudName : state.widget.newCloudName,
				uploadPreset: (state) => isNullOrUndefined(state.widget.newUploadPreset) ? state.widget.uploadPreset : state.widget.newUploadPreset,
			}),
		},

		methods: {
			...mapActions("widget", [
				widgetTypes.SET_ADVANCED_PARAM,
				widgetTypes.SET_CLOUD_PRESET,
				widgetTypes.SET_NEW_CLOUD_PRESET,
				widgetTypes.TOGGLE_WIDGET_MODAL]),
			...mapActions("app", [appTypes.SET_NOTIFICATION]),

			showUpdatedNotification() {
				this[appTypes.SET_NOTIFICATION]({
					type: consts.NOTICE_TYPES.SUCCESS,
					text: "Widget configuration updated"
				});
			},

			onBooleanChange(value, name) {
				this[widgetTypes.SET_ADVANCED_PARAM]({name, value});
				this.showUpdatedNotification();
			},

			onModalChange(){
				this[widgetTypes.TOGGLE_WIDGET_MODAL]();
				this.showUpdatedNotification();
			},

			onDefaultChange(e) {
				this[widgetTypes.SET_ADVANCED_PARAM]({
					name: "defaultSource",
					value: e.target.value
				});
				this.showUpdatedNotification();
			},

			validateCloudPresetInputs(showError = false) {
				let isValid = false;

				const cloudName = trim(this.$refs.cloudName.value),
					uploadPreset = trim(this.$refs.uploadPreset.value);

				this[widgetTypes.SET_NEW_CLOUD_PRESET]({
					cloudName,
					uploadPreset,
				});

				if (cloudName && uploadPreset) {
					this.cloudPresetInputError = false;
					this.cloudPresetApplyEnabled = true;
					isValid = true;
				}
				else {
					if (showError) {
						this[appTypes.SET_NOTIFICATION]({
							type: consts.NOTICE_TYPES.ERROR,
							text: "Please supply cloud name and upload preset name (unsigned)"
						});

						this.cloudPresetInputError = true;
					}

					this.cloudPresetApplyEnabled = false;
				}

				return isValid;
			},

			onCloudNameChange() {
				this.validateCloudPresetInputs();
			},

			onPresetChange() {
				this.validateCloudPresetInputs();
			},

			onTextEnter() {
				this.onTextFieldsApply();
			},

			onTextFieldsApply() {
				if (this.validateCloudPresetInputs(true)) {

					const cloudName = trim(this.newCloudName) || this.cloudName,
						uploadPreset = trim(this.newUploadPreset) || this.uploadPreset;

					this[widgetTypes.SET_CLOUD_PRESET]({cloudName, uploadPreset}); //set the new values to be used by the widget

					this[widgetTypes.SET_NEW_CLOUD_PRESET]({ //reset the new values
						cloudName: null,
						uploadPreset: null,
					});

					this.cloudPresetApplyEnabled = false;
					this.showUpdatedNotification();
				}
			},
		}
	}
</script>

<style lang="scss" scoped>
	@import "../../../assets/scss/_variables.scss";

	.block-options {
		padding: 17px 0;

		.button-container {
			margin: 12px 8px;
			border-bottom: 1px solid #c6d1db;
			padding-bottom: 10px;
		}

		.title {
			margin: 0 0 13px;
			letter-spacing: 1.1px;
		}

		.field-area {
			position: relative;
			overflow: hidden;
			display: flex;
			flex-direction: row;
			align-items: center;
			margin: 0 0 17px;
		}

		.label-holder {
			margin: 0 15px 0 0;
			font-weight: 500;
		}

		.field {
			max-width: 210px;
			width: 100%;
		}

		input.error {
			border: 1px solid $red;
		}

		input[type="text"] {
			padding: 5px 8px;
		}
	}


</style>
